import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles/ChatAssistant.css';

const ChatAssistant = () => {
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi! I'm your AI Loan Assistant. Let’s see what you qualify for! What’s your name?" }
  ]);
  const [input, setInput] = useState('');
  const [stage, setStage] = useState(1);
  const [leadData, setLeadData] = useState({});
  const [retry, setRetry] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const validateInput = (value) => {
    switch (stage) {
      case 1: // Name
        return /^[a-zA-Z\s]{2,}$/.test(value.trim());
      case 2: // Credit Score
        const credit = parseInt(value);
        return !isNaN(credit) && credit >= 300 && credit <= 850;
      case 3: // Loan Amount
        const loan = parseInt(value.replace(/[^0-9]/g, ''));
        return !isNaN(loan) && loan > 5000;
      case 4: // Email
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
      case 5: // Phone Number
        return /^\d{10}$/.test(value.replace(/\D/g, ''));
      default:
        return true;
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const newMessages = [...messages, { from: 'user', text: input }];
    setMessages(newMessages);

    let reply = '';
    const updatedLead = { ...leadData };

    // Validation
    if (!validateInput(input)) {
      switch (stage) {
        case 1:
          reply = "Please enter your full name (letters only).";
          break;
        case 2:
          reply = "Hmm, that doesn’t look right. What’s your estimated credit score (300–850)?";
          break;
        case 3:
          reply = "Please enter a valid loan amount (more than $5,000).";
          break;
        case 4:
          reply = "Please enter a valid email address.";
          break;
        case 5:
          reply = "Please enter a valid 10-digit phone number.";
          break;
        default:
          reply = "Let’s try that again.";
      }
      setMessages([...newMessages, { from: 'bot', text: reply }]);
      setInput('');
      setRetry(true);
      return;
    }

    setRetry(false);

    // Store data
    switch (stage) {
      case 1:
        updatedLead.name = input.trim();
        reply = `Great, ${updatedLead.name}! What’s your estimated credit score?`;
        break;
      case 2:
        updatedLead.creditScore = parseInt(input);
        reply = "Thanks! What’s your desired loan amount?";
        break;
      case 3:
        updatedLead.loanAmount = parseInt(input.replace(/[^0-9]/g, ''));
        reply = "Got it. What’s your email address?";
        break;
      case 4:
        updatedLead.email = input.trim();
        reply = "Almost done! What’s your phone number?";
        break;
      case 5:
        updatedLead.phoneNumber = input.replace(/\D/g, '');
        reply = "Perfect! Submitting your information now...";
        break;
      default:
        reply = "You're all set!";
    }

    // Show bot reply
    setMessages([...newMessages, { from: 'bot', text: reply }]);
    setInput('');
    setLeadData(updatedLead);

    if (stage === 5) {
      try {
        await axios.post('https://homeleadiq.onrender.com/api/leads/public', {
          ...updatedLead,
          source: 'Chat Assistant',
          notes: 'Submitted via AI Chat Assistant',
        });
        setMessages(prev => [
          ...prev,
          { from: 'bot', text: "You're all set! A loan officer will reach out shortly." }
        ]);
      } catch (err) {
        console.error("Error saving lead:", err);
        setMessages(prev => [
          ...prev,
          { from: 'bot', text: "Oops! Something went wrong while saving your info. Please try again later." }
        ]);
      }
    }

    setStage(prev => prev + 1);
  };

  return (
    <div className="chat-box">
      <div className="chat-messages" ref={chatRef}>
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-message ${msg.from}`}>
            {msg.text}
          </div>
        ))}
      </div>
      <div className="chat-input">
        <input
          type="text"
          placeholder="Type your response..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
        />
        <button onClick={handleSend}>Send</button>
      </div>
    </div>
  );
};

export default ChatAssistant;